import React from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Pause,
  Play,
  Shield,
  ShieldQuestion,
  Users,
  XCircle,
  User,
  HardHat,
  AlertCircle,
  HelpCircle,
  Activity,
  LucideIcon,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  CrewStatus,
  TicketPriority,
  TicketStatus,
  UserRole,
} from '@packages/database/types';

export const normalizeEnumText = (text: string): string => {
  if (!text) return '';
  return text
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// ============================================================================
// CONFIGURATIONS
// ============================================================================

type BadgeConfig = {
  label: string;
  icon: LucideIcon;
  className: string;
};

// --- TICKET STATUS CONFIG ---
const TICKET_STATUS_CONFIG: Record<TicketStatus, BadgeConfig> = {
  [TicketStatus.PENDING]: {
    label: 'Pending',
    icon: AlertTriangle,
    // Light: Rich Amber (100) | Dark: Glowing Amber
    className:
      'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800/50',
  },
  [TicketStatus.EN_ROUTE]: {
    label: 'En Route',
    icon: ShieldQuestion,
    // Light: Rich Orange (100) | Dark: Glowing Orange
    className:
      'bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-800/50',
  },
  [TicketStatus.ON_SITE]: {
    label: 'On Site',
    icon: Users,
    // Light: Rich Blue (100) | Dark: Glowing Blue
    className:
      'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800/50',
  },
  [TicketStatus.IN_PROGRESS]: {
    label: 'In Progress',
    icon: Play,
    // Light: Rich Indigo (100) | Dark: Glowing Indigo
    className:
      'bg-indigo-100 text-indigo-800 border-indigo-300 dark:bg-indigo-900/30 dark:text-indigo-400 dark:border-indigo-800/50',
  },
  [TicketStatus.COMPLETED]: {
    label: 'Completed',
    icon: CheckCircle2,
    // Light: Rich Emerald (100) | Dark: Glowing Emerald
    className:
      'bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800/50',
  },
  [TicketStatus.ON_HOLD]: {
    label: 'On Hold',
    icon: Pause,
    // Light: Rich Purple (100) | Dark: Glowing Purple
    className:
      'bg-purple-100 text-purple-800 border-purple-300 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-800/50',
  },
  [TicketStatus.CANCELLED]: {
    label: 'Cancelled',
    icon: XCircle,
    // Light: Rich Slate (100) | Dark: Ghost Slate
    className:
      'bg-slate-100 text-slate-800 border-slate-300 dark:bg-slate-800/50 dark:text-slate-400 dark:border-slate-700',
  },
  // [TicketStatus.VERIFIED]: {
  //   label: 'Verified',
  //   icon: CheckCircle2,
  //   className:
  //     'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800/50',
  // },
  [TicketStatus.FIELD_COMPLETE]: {
    label: 'Field Complete',
    icon: CheckCircle2,
    className:
      'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800/50',
  },
  [TicketStatus.REJECTED]: {
    label: 'Rejected',
    icon: XCircle,
    className:
      'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800/50',
  },
};
// --- TICKET PRIORITY CONFIG ---
const PRIORITY_CONFIG: Record<TicketPriority, BadgeConfig> = {
  [TicketPriority.URGENT]: {
    label: 'Urgent',
    icon: AlertCircle,
    // Light: Rich Red (100)
    className:
      'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800/50',
  },
  [TicketPriority.HIGH]: {
    label: 'High',
    icon: AlertTriangle,
    // Light: Rich Orange (100)
    className:
      'bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-800/50',
  },
  [TicketPriority.MEDIUM]: {
    label: 'Medium',
    icon: Activity,
    // Light: Rich Blue (100)
    className:
      'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800/50',
  },
  [TicketPriority.LOW]: {
    label: 'Low',
    icon: HelpCircle,
    // Light: Rich Slate (100)
    className:
      'bg-slate-100 text-slate-800 border-slate-300 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700',
  },
};

// --- CREW STATUS CONFIG ---
const CREW_STATUS_CONFIG: Record<CrewStatus, BadgeConfig> = {
  [CrewStatus.AVAILABLE]: {
    label: 'Available',
    icon: CheckCircle2,
    className: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20',
  },
  [CrewStatus.BUSY]: {
    label: 'Busy',
    icon: AlertTriangle,
    className: 'bg-muted text-muted-foreground border-border',
  },
  [CrewStatus.UNAVAILABLE]: {
    label: 'Unavailable',
    icon: XCircle,
    className: 'bg-red-500/10 text-red-600 border-red-500/20',
  },
};

// --- USER ROLE CONFIG ---
const ROLE_CONFIG: Record<UserRole, BadgeConfig> = {
  [UserRole.ADMIN]: {
    label: 'Admin',
    icon: Shield,
    className:
      'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800/50',
  },
  [UserRole.CREW]: {
    label: 'Crew',
    icon: HardHat,
    className:
      'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800/50',
  },
  //@ts-ignore
  [UserRole.USER]: {
    label: 'User',
    icon: User,
    className:
      'bg-slate-100 text-slate-800 border-slate-300 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700',
  },
};

// --- PROJECT STATUS CONFIG ---
const PROJECT_STATUS_CONFIG: Record<string, BadgeConfig> = {
  COMPLETED: {
    label: 'Completed',
    icon: CheckCircle2,
    className:
      'bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800/50',
  },
  IN_PROGRESS: {
    label: 'In Progress',
    icon: Play,
    className:
      'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800/50',
  },
  NOT_STARTED: {
    label: 'Not Started',
    icon: Pause,
    className:
      'bg-slate-100 text-slate-800 border-slate-300 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700',
  },
  PENDING: {
    label: 'Pending',
    icon: Clock,
    className:
      'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800/50',
  },
};

// ============================================================================
// 3. BASE COMPONENT (Internal)
// ============================================================================

interface GenericBadgeProps {
  config: BadgeConfig;
  showIcon?: boolean;
  showLabel?: boolean;
  className?: string;
  size?: 'sm' | 'default' | 'lg';
}

const GenericBadge: React.FC<GenericBadgeProps> = ({
  config,
  showIcon = true,
  showLabel = true,
  className,
  size = 'default',
}) => {
  const Icon = config.icon;

  const sizeClasses = {
    sm: 'text-[10px] px-1.5 h-5',
    default: 'text-xs px-2.5 py-0.5',
    lg: 'text-sm px-3 py-1',
  };

  const iconSizes = {
    sm: 'h-3 w-3',
    default: 'h-3.5 w-3.5',
    lg: 'h-4 w-4',
  };

  return (
    <Badge
      variant="outline"
      className={cn(
        'gap-1.5 font-medium transition-colors border shadow-sm',
        config.className,
        sizeClasses[size],
        className,
      )}
    >
      {showIcon && <Icon className={cn(iconSizes[size], 'opacity-80')} />}
      {showLabel && <span>{config.label}</span>}
    </Badge>
  );
};

// ============================================================================
// 4. EXPORTED BADGE COMPONENTS
// ============================================================================

interface BadgeProps {
  className?: string;
  size?: 'sm' | 'default' | 'lg';
  showIcon?: boolean;
  showLabel?: boolean;
}

// --- TICKET STATUS BADGE ---
interface TicketStatusBadgeProps extends BadgeProps {
  status: TicketStatus;
}
export const StatusBadge: React.FC<TicketStatusBadgeProps> = ({
  status,
  ...props
}) => {
  const config =
    TICKET_STATUS_CONFIG[status] || TICKET_STATUS_CONFIG[TicketStatus.PENDING];
  return <GenericBadge config={config} {...props} />;
};

// --- PRIORITY BADGE ---
interface PriorityBadgeProps extends BadgeProps {
  priority: TicketPriority;
}
export const PriorityBadge: React.FC<PriorityBadgeProps> = ({
  priority,
  ...props
}) => {
  const config =
    PRIORITY_CONFIG[priority] || PRIORITY_CONFIG[TicketPriority.LOW];
  return <GenericBadge config={config} {...props} />;
};

// --- CREW STATUS BADGE ---
interface CrewStatusBadgeProps extends BadgeProps {
  status: CrewStatus;
}
export const CrewStatusBadge: React.FC<CrewStatusBadgeProps> = ({
  status,
  ...props
}) => {
  const config =
    CREW_STATUS_CONFIG[status] || CREW_STATUS_CONFIG[CrewStatus.UNAVAILABLE];
  return <GenericBadge showIcon={true} config={config} {...props} />;
};

// --- ROLE BADGE ---
interface RoleBadgeProps extends BadgeProps {
  role: UserRole;
}
export const RoleBadge: React.FC<RoleBadgeProps> = ({ role, ...props }) => {
  //@ts-ignore
  const config = ROLE_CONFIG[role] || ROLE_CONFIG[UserRole.USER];
  return <GenericBadge config={config} {...props} />;
};

// --- PROJECT STATUS BADGE ---
interface ProjectStatusBadgeProps extends BadgeProps {
  status: string | any;
}
export const ProjectStatusBadge: React.FC<ProjectStatusBadgeProps> = ({
  status,
  ...props
}) => {
  const key = String(status).toUpperCase();
  const config = PROJECT_STATUS_CONFIG[key] || PROJECT_STATUS_CONFIG.PENDING;
  return <GenericBadge config={config} {...props} />;
};

// ============================================================================
// 5. LEGACY EXPORTS (Backward Compatibility)
// ============================================================================

export const getStatusText = (status: TicketStatus) =>
  TICKET_STATUS_CONFIG[status]?.label || 'Unknown';
export const getStatusIcon = (status: TicketStatus) => {
  const Icon =
    TICKET_STATUS_CONFIG[status]?.icon || TICKET_STATUS_CONFIG.PENDING.icon;
  return <Icon className="h-4 w-4" />;
};

export const getPriorityText = (priority: TicketPriority) =>
  PRIORITY_CONFIG[priority]?.label || 'Unknown';

export const getRoleText = (role: UserRole) =>
  ROLE_CONFIG[role]?.label || 'User';

export const getSkillText = normalizeEnumText;

export const getCrewStatusText = (status: CrewStatus) =>
  CREW_STATUS_CONFIG[status]?.label || 'Unknown';

export const getCrewStatusIcon = (status: CrewStatus) => {
  const Icon =
    CREW_STATUS_CONFIG[status]?.icon ||
    CREW_STATUS_CONFIG[CrewStatus.UNAVAILABLE].icon;
  return <Icon className="h-4 w-4" />;
};
